import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectTrigger, SelectContent, SelectItem, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Check, ChevronRight } from "lucide-react";

const STEPS = ["Azienda", "Strategia Fiscale", "Processi", "Team TCF", "Sintesi"];
const STRATEGY_PLACEHOLDER = "La società [Nome] adotta una strategia di gestione del rischio fiscale orientata alla conformità sostanziale alle norme tributarie, alla trasparenza verso l'Amministrazione Finanziaria e alla gestione proattiva delle situazioni di incertezza interpretativa…";

const CORPORATE_STRUCTURES = [
  { v: "standalone", l: "Indipendente" },
  { v: "holding", l: "Holding" },
  { v: "controllata", l: "Controllata" },
  { v: "gruppo", l: "Parte di gruppo" },
];

export default function Onboarding() {
  const { org, updateOrg } = useAuth();
  const nav = useNavigate();
  const [step, setStep] = useState(0);
  const [processes, setProcesses] = useState([]);
  const [data, setData] = useState({
    name: org?.name || "", vat_number: org?.vat_number || "", ateco_code: "",
    operating_countries: ["IT"], corporate_structure: "standalone",
    fiscal_year: new Date().getFullYear(),
    fiscal_strategy: "", fiscal_strategy_approval_date: "", fiscal_strategy_approved_by: "",
    selected_processes: [],
    tax_manager_email: "", auditor_email: "", consultant_email: "",
  });
  const upd = (k, v) => setData((d) => ({ ...d, [k]: v }));

  useEffect(() => {
    api.get("/processes").then(({ data }) => {
      setProcesses(data);
      setData((d) => ({ ...d, selected_processes: data.map((p) => p.code) }));
    });
  }, []);

  useEffect(() => {
    if (org && org.setup_completed) nav("/");
  }, [org, nav]);

  const toggleProcess = (code) => {
    setData((d) => ({
      ...d,
      selected_processes: d.selected_processes.includes(code)
        ? d.selected_processes.filter((c) => c !== code)
        : [...d.selected_processes, code],
    }));
  };

  const next = async () => {
    if (step < STEPS.length - 1) { setStep(step + 1); return; }
    try {
      const { data: res } = await api.post("/organizations/complete-onboarding", data);
      updateOrg(res.organization);
      toast.success(`TCF inizializzato — ${res.risks_initialized} rischi pronti`);
      nav("/");
    } catch (e) {
      toast.error("Errore salvataggio");
    }
  };

  const back = () => step > 0 && setStep(step - 1);

  return (
    <div className="min-h-screen bg-slate-50 py-12 px-6" data-testid="onboarding-page">
      <div className="max-w-3xl mx-auto">
        <div className="font-display font-extrabold text-2xl text-slate-900 mb-2">TCF<span className="text-slate-400">.</span>Platform</div>
        <h1 className="font-display text-4xl font-bold tracking-tight text-slate-900 mt-6">Configura il tuo TCF</h1>
        <p className="text-slate-600 mt-2">5 passaggi rapidi per costruire la struttura del tuo Tax Control Framework.</p>

        <div className="flex items-center gap-2 mt-10">
          {STEPS.map((label, i) => (
            <div key={label} className="flex-1 flex items-center gap-2">
              <div className={`w-8 h-8 rounded-sm border flex items-center justify-center text-sm font-medium ${i <= step ? "bg-slate-900 border-slate-900 text-white" : "bg-white border-slate-300 text-slate-400"}`}>
                {i < step ? <Check size={14} /> : i + 1}
              </div>
              <div className={`text-xs font-medium ${i <= step ? "text-slate-900" : "text-slate-400"}`}>{label}</div>
              {i < STEPS.length - 1 && <div className="flex-1 h-px bg-slate-200" />}
            </div>
          ))}
        </div>

        <div className="card-flat p-8 mt-8" data-testid="onboarding-card">
          {step === 0 && (
            <div className="space-y-4">
              <h2 className="font-display text-xl font-semibold text-slate-900">Informazioni azienda</h2>
              <div className="grid grid-cols-2 gap-4">
                <div><Label className="label-tag">Ragione sociale</Label><Input value={data.name} onChange={(e) => upd("name", e.target.value)} className="mt-1 rounded-sm" data-testid="ob-name" /></div>
                <div><Label className="label-tag">P. IVA</Label><Input value={data.vat_number} onChange={(e) => upd("vat_number", e.target.value)} className="mt-1 rounded-sm" data-testid="ob-vat" /></div>
                <div><Label className="label-tag">Codice ATECO</Label><Input value={data.ateco_code} onChange={(e) => upd("ateco_code", e.target.value)} className="mt-1 rounded-sm" placeholder="es. 62.01.00" data-testid="ob-ateco" /></div>
                <div>
                  <Label className="label-tag">Struttura societaria</Label>
                  <Select value={data.corporate_structure} onValueChange={(v) => upd("corporate_structure", v)}>
                    <SelectTrigger className="mt-1 rounded-sm"><SelectValue /></SelectTrigger>
                    <SelectContent>{CORPORATE_STRUCTURES.map((s) => <SelectItem key={s.v} value={s.v}>{s.l}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div><Label className="label-tag">Esercizio fiscale</Label><Input type="number" value={data.fiscal_year} onChange={(e) => upd("fiscal_year", parseInt(e.target.value))} className="mt-1 rounded-sm" /></div>
              </div>
              {org?.is_pmi && (
                <div className="bg-amber-50 border border-amber-200 p-3 text-sm text-amber-900 rounded-sm">
                  Sei nel regime TCF opzionale per PMI. Il sistema attiverà la guida semplificata.
                </div>
              )}
            </div>
          )}

          {step === 1 && (
            <div className="space-y-4">
              <h2 className="font-display text-xl font-semibold text-slate-900">Strategia fiscale</h2>
              <p className="text-sm text-slate-600">Descrivi la strategia fiscale approvata dal CDA.</p>
              <Textarea rows={10} value={data.fiscal_strategy} onChange={(e) => upd("fiscal_strategy", e.target.value)} placeholder={STRATEGY_PLACEHOLDER} className="rounded-sm" data-testid="ob-strategy" />
              <div className="grid grid-cols-2 gap-4">
                <div><Label className="label-tag">Data approvazione</Label><Input type="date" value={data.fiscal_strategy_approval_date} onChange={(e) => upd("fiscal_strategy_approval_date", e.target.value)} className="mt-1 rounded-sm" /></div>
                <div><Label className="label-tag">Approvata da</Label><Input value={data.fiscal_strategy_approved_by} onChange={(e) => upd("fiscal_strategy_approved_by", e.target.value)} className="mt-1 rounded-sm" placeholder="es. CDA del 15/03/2025" /></div>
              </div>
            </div>
          )}

          {step === 2 && (
            <div className="space-y-4">
              <h2 className="font-display text-xl font-semibold text-slate-900">Processi applicabili</h2>
              <p className="text-sm text-slate-600">Seleziona i 7 processi fiscali rilevanti per la tua azienda.</p>
              <div className="grid grid-cols-2 gap-3">
                {processes.map((p) => {
                  const checked = data.selected_processes.includes(p.code);
                  return (
                    <label key={p.code} className={`border rounded-sm p-4 cursor-pointer transition-colors ${checked ? "border-slate-900 bg-slate-50" : "border-slate-200"}`} data-testid={`process-card-${p.code}`}>
                      <div className="flex items-start gap-3">
                        <Checkbox checked={checked} onCheckedChange={() => toggleProcess(p.code)} className="mt-1" />
                        <div>
                          <div className="font-medium text-slate-900">{p.name}</div>
                          <div className="text-xs text-slate-600 mt-1">{p.description}</div>
                        </div>
                      </div>
                    </label>
                  );
                })}
              </div>
            </div>
          )}

          {step === 3 && (
            <div className="space-y-4">
              <h2 className="font-display text-xl font-semibold text-slate-900">Team TCF</h2>
              <div className="space-y-3">
                <div><Label className="label-tag">Tax Manager (email)</Label><Input value={data.tax_manager_email} onChange={(e) => upd("tax_manager_email", e.target.value)} className="mt-1 rounded-sm" /></div>
                <div><Label className="label-tag">Internal Auditor (email)</Label><Input value={data.auditor_email} onChange={(e) => upd("auditor_email", e.target.value)} className="mt-1 rounded-sm" /></div>
                <div><Label className="label-tag">Consulente esterno (email)</Label><Input value={data.consultant_email} onChange={(e) => upd("consultant_email", e.target.value)} className="mt-1 rounded-sm" /></div>
              </div>
            </div>
          )}

          {step === 4 && (
            <div className="space-y-4" data-testid="ob-summary">
              <h2 className="font-display text-xl font-semibold text-slate-900">Sintesi</h2>
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div className="card-flat p-4"><div className="label-tag">Organizzazione</div><div className="mt-1 text-slate-900 font-medium">{data.name}</div></div>
                <div className="card-flat p-4"><div className="label-tag">Processi selezionati</div><div className="mt-1 text-slate-900 font-medium data-num">{data.selected_processes.length} / {processes.length}</div></div>
                <div className="card-flat p-4"><div className="label-tag">Anno fiscale</div><div className="mt-1 text-slate-900 font-medium data-num">{data.fiscal_year}</div></div>
                <div className="card-flat p-4"><div className="label-tag">Strategia firmata</div><div className="mt-1 text-slate-900 font-medium">{data.fiscal_strategy_approved_by || "—"}</div></div>
              </div>
              <p className="text-sm text-slate-600">Cliccando "Avvia il TCF", il sistema inizializzerà la mappa rischi per i processi selezionati.</p>
            </div>
          )}

          <div className="flex justify-between mt-8">
            <Button variant="outline" onClick={back} disabled={step === 0} className="rounded-sm">Indietro</Button>
            <Button onClick={next} className="bg-slate-900 hover:bg-slate-800 rounded-sm" data-testid="ob-next">
              {step === STEPS.length - 1 ? "Avvia il TCF" : "Continua"} <ChevronRight size={16} className="ml-1" />
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
